// ============================================================================
// client-identity-match.js — Stage 1 ↔ CRM identity resolver
// ----------------------------------------------------------------------------
// Surfaces, for the current project, WHICH CRM records (Requester company +
// Site/property + Strata Plan) this quote is tied to — and whether each is a
// MATCH to an existing record or a NEW record created for this job. Drives the
// CRM line on the Stage 1 Requester and Site cards (Stage1JobDetails), which
// deep-links back to the Clients page ("Client & Site Intelligence").
//
// Identity is an explicit operator decision persisted on the project. Read the
// selected company/site without repeating name, email-domain or SP heuristics.
// A zero linked-history count does not establish when the record was created.
//
// PURE + TESTABLE. `buildClientIdentity` takes already-loaded records (company,
// property, allProjects) and returns a serialisable view-model. The async
// loading (DataService.companies.getById / properties.getById) lives in the
// caller (App.jsx), mirroring the `getActiveBiosMemo` pattern, so this stays
// free of app/network imports and unit-testable.
//
// Single-source-of-truth: this module NEVER writes. Record creation stays on the
// explicit matching flow. Flag-gated by `qm_client_identity_card` in the caller.
// ============================================================================

import { normalizeStrataPlan } from '../utils/address.js';

// Inbound CRM/project/formData records arrive snake_case (Dexie) or camelCase
// (API) with no SQL/contract-stable shape, and are read via deep optional
// chaining, so `Record<string, any>` is the honest boundary type (mirrors the
// M3-S19 scope-seed `LooseRecord` precedent).
type LooseRecord = Record<string, any>;

/** Field-shape defensive accessor: project records arrive snake_case (Dexie) or
 *  camelCase (API). */
function projCompanyId(p: LooseRecord | null | undefined): string | null { return p?.company_id ?? p?.companyId ?? null; }
function projPropertyId(p: LooseRecord | null | undefined): string | null { return p?.property_id ?? p?.propertyId ?? null; }

/** The strata plan string the user entered. formData uses lowercase `strataplan`;
 *  the AI extractor + some reads use camelCase `strataPlan`. Read both. */
export function readStrataPlanString(formData: LooseRecord | null | undefined): string {
    const raw = (formData?.strataplan ?? formData?.strataPlan ?? '').toString().trim();
    return raw || '';
}

/**
 * Build the Client & Site identity view-model for a project.
 *
 * @param {object} args
 * @param {object} args.project      The current project record (carries company_id / property_id / id).
 * @param {object} args.formData     Stage-1 form data (client name/email, address, strataplan).
 * @param {object|null} args.company The linked Requester company (from companies.getById), or null.
 * @param {object|null} args.property The linked Site/property (from properties.getById), or null.
 * @param {Array}  [args.allProjects] All projects (to count prior quotes for the company/site).
 * @returns {object} identity view-model (see shape below). Never throws.
 */
export interface ClientIdentityDeepLink {
    page: string;
    pageNavOptions: Record<string, unknown>;
}

export interface ClientIdentityBlock {
    id: string | null;
    name: string;
    type?: string | null;
    address?: string;
    matched: boolean;
    isNew: boolean;
    priorQuoteCount: number;
    deepLink: ClientIdentityDeepLink | null;
}

export interface StrataIdentityBlock {
    raw: string;
    norm: string;
    matchedViaSite: boolean;
}

export interface ClientIdentity {
    hasData: boolean;
    client: ClientIdentityBlock | null;
    site: ClientIdentityBlock | null;
    strataPlan: StrataIdentityBlock | null;
}

export interface BuildClientIdentityArgs {
    project?: LooseRecord | null;
    formData?: LooseRecord | null;
    company?: LooseRecord | null;
    property?: LooseRecord | null;
    allProjects?: LooseRecord[];
}

export function buildClientIdentity({ project, formData, company, property, allProjects }: BuildClientIdentityArgs = {}): ClientIdentity {
    const projects = Array.isArray(allProjects) ? allProjects : [];
    const currentId = project?.id ?? null;
    const fd = formData || {};

    // ── Client (Requester company) ──────────────────────────────────────────
    const companyLinked = !!(company && company.id);
    const companyName = companyLinked
        ? (company.name || company.companyName || '')
        : (fd.requesterCompany || fd.clientCompany || '').trim();
    let clientBlock: ClientIdentityBlock | null = null;
    if (companyLinked || companyName) {
        const priorQuoteCount = companyLinked
            ? projects.filter(p => projCompanyId(p) === company.id && p.id !== currentId).length
            : 0;
        clientBlock = {
            id: companyLinked ? company.id : null,
            name: companyName || 'Unknown Requester',
            type: companyLinked ? (company.companyType || company.company_type || null) : null,
            matched: companyLinked,
            // Legacy compatibility flag means no prior linked quotes, not a
            // promise that this company was newly created or will be created.
            isNew: !companyLinked || priorQuoteCount === 0,
            priorQuoteCount,
            deepLink: companyLinked
                ? { page: 'clients', pageNavOptions: { crmView: 'requesters', companyId: company.id } }
                : null,
        };
    }

    // ── Site (property) ─────────────────────────────────────────────────────
    const propertyLinked = !!(property && property.id);
    const siteName = propertyLinked
        ? (property.name || '')
        : '';
    const siteAddress = propertyLinked
        ? (property.addressLine1 || property.address_line1 || property.address || '')
        : [fd.address, fd.suburb].filter(Boolean).join(', ');
    let siteBlock: ClientIdentityBlock | null = null;
    if (propertyLinked || (fd.address || '').trim()) {
        const priorQuoteCount = propertyLinked
            ? projects.filter(p => projPropertyId(p) === property.id && p.id !== currentId).length
            : 0;
        siteBlock = {
            id: propertyLinked ? property.id : null,
            name: siteName || siteAddress || 'Site',
            address: siteAddress,
            matched: propertyLinked,
            isNew: !propertyLinked || priorQuoteCount === 0,
            priorQuoteCount,
            deepLink: propertyLinked
                ? { page: 'clients', pageNavOptions: { crmView: 'sites', detailId: property.id } }
                : null,
        };
    }

    // ── Strata Plan # ───────────────────────────────────────────────────────
    const spRaw = readStrataPlanString(fd);
    const spNorm = spRaw ? normalizeStrataPlan(spRaw) : '';
    const propSpNorm = propertyLinked
        ? normalizeStrataPlan(property.strataPlan || property.strata_plan || '')
        : '';
    let strataBlock: StrataIdentityBlock | null = null;
    if (spRaw) {
        strataBlock = {
            raw: spRaw,
            norm: spNorm,
            // The linked Site carries this same normalised SP → the plan is
            // anchored to a known site. (A first-class strata_plans entity match
            // is a follow-up once the strata-plans route lands.)
            matchedViaSite: !!(propSpNorm && propSpNorm === spNorm),
        };
    }

    return {
        hasData: !!(clientBlock || siteBlock || strataBlock),
        client: clientBlock,
        site: siteBlock,
        strataPlan: strataBlock,
    };
}
